import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import type {
  ClassificationBandRequest,
  ClassificationSchemeRequest,
  ProvisionBasis,
} from '@/api/loans-types'
import type { DualControlResult } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { PROVISION_BASIS_LABELS, parsePercent, percentToFraction } from './constants'
import { DualControlNotice, DualControlOutcome } from './dual-control'
import { LOAN_PERMISSIONS, useLoanPermissions } from './permissions'
import { loanPaths, useLoanNavigate } from './loan-paths'
import { DateField, FormField, NoteField, NumberField, OperationForm } from './operation-form'
import { useCreateClassificationScheme } from './mutations'

/**
 * Putting classification buckets and provisioning rates into force.
 *
 * This is the most consequential configuration in the module: it decides what the institution
 * reports as non-performing and how much it has to hold against every facility. It is
 * countersigned, and it takes effect from a date rather than immediately — a scheme is a
 * regulatory position, and the date it started applying from is part of it.
 *
 * A scheme is never edited. A new one supersedes the last from its effective date, so the history
 * of what was reported under which rules stays readable.
 */

interface BandDraft extends Omit<ClassificationBandRequest, 'provisionRate' | 'maxDaysOverdue'> {
  key: string
  /** Percent as keyed. Converted to the fraction the wire carries at submission. */
  provisionPercent: string
  /** Empty means open-ended — the worst band has no upper bound. */
  maxDaysOverdue: string
}

const newKey = () => Math.random().toString(36).slice(2)

const emptyBand = (): BandDraft => ({
  key: newKey(),
  classCode: '',
  className: '',
  minDaysOverdue: 0,
  maxDaysOverdue: '',
  provisionPercent: '',
  suspendsInterestAccrual: false,
  isPerforming: false,
})

export function ClassificationSchemeForm() {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const create = useCreateClassificationScheme()

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [isProvisional, setIsProvisional] = useState(true)
  const [sourceReference, setSourceReference] = useState('')
  const [provisionBasis, setProvisionBasis] = useState<ProvisionBasis>('PrincipalOutstanding')
  const [floorClassCode, setFloorClassCode] = useState('')
  const [coolingDays, setCoolingDays] = useState('')
  const [bands, setBands] = useState<BandDraft[]>([emptyBand()])
  const [result, setResult] = useState<DualControlResult | null>(null)

  const updateBand = (key: string, patch: Partial<BandDraft>) => {
    setBands((current) => current.map((band) => (band.key === key ? { ...band, ...patch } : band)))
  }

  const bandsValid =
    bands.length > 0 &&
    bands.every(
      (band) =>
        band.classCode.trim() !== '' &&
        band.className.trim() !== '' &&
        Number.isInteger(band.minDaysOverdue) &&
        band.minDaysOverdue >= 0 &&
        (band.maxDaysOverdue.trim() === '' ||
          Number.isInteger(Number.parseInt(band.maxDaysOverdue, 10))) &&
        Number.isFinite(parsePercent(band.provisionPercent)) &&
        parsePercent(band.provisionPercent) >= 0 &&
        parsePercent(band.provisionPercent) <= 100,
    )

  const valid = code.trim() !== '' && name.trim() !== '' && effectiveFrom !== '' && bandsValid

  const build = (): ClassificationSchemeRequest => ({
    code: code.trim(),
    name: name.trim(),
    effectiveFrom,
    isProvisional,
    sourceReference: sourceReference.trim() === '' ? null : sourceReference.trim(),
    provisionBasis,
    restructuredFloorClassCode: floorClassCode.trim() === '' ? null : floorClassCode.trim(),
    restructuredCoolingDays:
      coolingDays.trim() === '' ? null : Number.parseInt(coolingDays, 10),
    bands: bands.map((band) => ({
      classCode: band.classCode.trim(),
      className: band.className.trim(),
      minDaysOverdue: band.minDaysOverdue,
      // Empty is null, and null is what the open-ended worst band means. Sent explicitly.
      maxDaysOverdue:
        band.maxDaysOverdue.trim() === '' ? null : Number.parseInt(band.maxDaysOverdue, 10),
      provisionRate: percentToFraction(parsePercent(band.provisionPercent)),
      suspendsInterestAccrual: band.suspendsInterestAccrual,
      isPerforming: band.isPerforming,
    })),
  })

  return (
    <OperationForm
      title="Put a classification scheme into force"
      description="The buckets a facility is reported in, and what has to be held against each of them."
      submitLabel="Submit the scheme"
      isPending={create.isPending}
      disabled={!valid}
      error={create.isError ? create.error : null}
      onDone={() => navigate(loanPaths.portfolio)}
      notice={
        <DualControlNotice
          action="Putting a scheme into force"
          approvePermission={LOAN_PERMISSIONS.classificationConfigApprove}
          canCountersign={permissions.canCountersignScheme}
        />
      }
      outcome={
        result ? (
          <DualControlOutcome
            result={result}
            awaitingDescription="The scheme is stored and is not in force. Classification runs still use whatever scheme was in force before."
            appliedDescription="The scheme is in force from its effective date. The next classification run will use it."
          />
        ) : null
      }
      onSubmit={() => {
        if (valid) {
          create.mutate(build(), { onSuccess: setResult })
        }
      }}
      footnote="A scheme is never edited. A new one supersedes it from its own effective date, which is what keeps the history of what was reported under which rules readable."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <NoteField
          id="scheme-code"
          label="Code"
          required
          value={code}
          onChange={setCode}
          placeholder="BOG-2026"
        />
        <NoteField
          id="scheme-name"
          label="Name"
          required
          value={name}
          onChange={setName}
          placeholder="Bank of Ghana provisioning, 2026"
        />
        <DateField
          id="scheme-effective"
          label="In force from"
          value={effectiveFrom}
          onChange={setEffectiveFrom}
          hint="One scheme per effective date. The server refuses a date already taken."
        />
        <FormField
          id="scheme-basis"
          label="Provision basis"
          required
          hint="What the provisioning rate is applied to."
        >
          <Select
            value={provisionBasis}
            onValueChange={(next) => setProvisionBasis(next as ProvisionBasis)}
          >
            <SelectTrigger id="scheme-basis">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(PROVISION_BASIS_LABELS) as ProvisionBasis[]).map((basis) => (
                <SelectItem key={basis} value={basis}>
                  {PROVISION_BASIS_LABELS[basis]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>

      <div className="flex items-start gap-2">
        <Checkbox
          id="scheme-provisional"
          checked={isProvisional}
          onCheckedChange={(checked) => setIsProvisional(checked === true)}
          className="mt-0.5"
        />
        <div className="space-y-0.5">
          <Label htmlFor="scheme-provisional" className="text-sm font-normal text-body">
            Provisional
          </Label>
          <p className="text-xs text-muted">
            In force before the directive it will be reconciled against has been confirmed. Every
            classification run against a provisional scheme says so on its result.
          </p>
        </div>
      </div>

      <NoteField
        id="scheme-source"
        label="Source reference"
        value={sourceReference}
        onChange={setSourceReference}
        placeholder="BoG Notice No. …"
        hint="The directive or circular this follows. Empty is sent as null, which is what provisional means."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <NoteField
          id="scheme-floor"
          label="Floor class for restructured facilities"
          value={floorClassCode}
          onChange={setFloorClassCode}
          placeholder="SUB"
          hint="The best class a restructured facility may be reported as. Empty imposes no floor."
        />
        <NumberField
          id="scheme-cooling"
          label="Cooling days"
          required={false}
          value={coolingDays}
          onChange={setCoolingDays}
          min={0}
          hint="How long a restructured facility is held at that floor before it can improve."
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-heading">Bands</p>
            <p className="text-xs text-muted">
              In days overdue, from best to worst. The worst band is left open-ended.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setBands((current) => [...current, emptyBand()])}
          >
            <Plus />
            Add a band
          </Button>
        </div>

        {bands.map((band, index) => (
          <div key={band.key} className="space-y-3 rounded-control border border-border-subtle p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-heading">Band {index + 1}</p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={bands.length === 1}
                onClick={() => setBands((current) => current.filter((row) => row.key !== band.key))}
                aria-label={`Remove band ${index + 1}`}
              >
                <Trash2 />
                Remove
              </Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <NoteField
                id={`band-code-${band.key}`}
                label="Class code"
                required
                value={band.classCode}
                onChange={(value) => updateBand(band.key, { classCode: value })}
                placeholder="OLEM"
              />
              <NoteField
                id={`band-name-${band.key}`}
                label="Class name"
                required
                value={band.className}
                onChange={(value) => updateBand(band.key, { className: value })}
                placeholder="Other loans especially mentioned"
              />
              <NumberField
                id={`band-min-${band.key}`}
                label="From days overdue"
                value={String(band.minDaysOverdue)}
                onChange={(value) =>
                  updateBand(band.key, { minDaysOverdue: Number.parseInt(value, 10) || 0 })
                }
                min={0}
              />
              <FormField
                id={`band-max-${band.key}`}
                label="To days overdue"
                hint="Leave empty for the open-ended worst band."
              >
                <Input
                  id={`band-max-${band.key}`}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={band.maxDaysOverdue}
                  onChange={(event) => updateBand(band.key, { maxDaysOverdue: event.target.value })}
                />
              </FormField>
              <FormField
                id={`band-rate-${band.key}`}
                label="Provision rate"
                required
                hint="A percentage of the provision basis. Sent as a fraction."
              >
                <div className="flex h-9 w-full items-center rounded-control border border-border-subtle bg-surface transition-colors focus-within:border-brand-text hover:border-border-strong">
                  <input
                    id={`band-rate-${band.key}`}
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min={0}
                    max={100}
                    value={band.provisionPercent}
                    onChange={(event) =>
                      updateBand(band.key, { provisionPercent: event.target.value })
                    }
                    className="numeric h-full w-full bg-transparent px-3 text-right text-sm text-heading tabular-nums outline-none"
                  />
                  <span className="pr-3 text-xs font-medium text-muted" aria-hidden>
                    %
                  </span>
                </div>
              </FormField>

              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id={`band-performing-${band.key}`}
                    checked={band.isPerforming}
                    onCheckedChange={(checked) =>
                      updateBand(band.key, { isPerforming: checked === true })
                    }
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={`band-performing-${band.key}`}
                    className="text-sm font-normal text-body"
                  >
                    Reported as performing
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id={`band-suspend-${band.key}`}
                    checked={band.suspendsInterestAccrual}
                    onCheckedChange={(checked) =>
                      updateBand(band.key, { suspendsInterestAccrual: checked === true })
                    }
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={`band-suspend-${band.key}`}
                    className="text-sm font-normal text-body"
                  >
                    Suspends interest accrual
                  </Label>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!bandsValid ? (
        <Alert tone="info">
          <AlertTitle>The bands are not complete</AlertTitle>
          <AlertDescription>
            Every band needs a code, a name, a starting day count and a provisioning rate. The
            server checks that the bands are contiguous and that exactly one is open-ended, and
            refuses the set rather than accepting part of it.
          </AlertDescription>
        </Alert>
      ) : null}
    </OperationForm>
  )
}
